import { useState, useCallback, useRef, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  addEdge,
  useNodesState,
  useEdgesState,
  type Connection,
  type NodeTypes,
  type OnConnect,
  type Node,
  BackgroundVariant,
  Panel,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { toast } from 'sonner'
import { useWorkflow } from '@/contexts/WorkflowContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Save, Play, Pause, Users } from 'lucide-react'
import { NodePalette } from '@/components/workflow/NodePalette'
import { NodeConfigPanel } from '@/components/workflow/NodeConfigPanel'
import { TriggerNode } from '@/components/workflow/nodes/TriggerNode'
import { ActionNode } from '@/components/workflow/nodes/ActionNode'
import { ConditionNode } from '@/components/workflow/nodes/ConditionNode'
import { DelayNode } from '@/components/workflow/nodes/DelayNode'
import { AgentNode } from '@/components/workflow/nodes/AgentNode'
import { ControlNode } from '@/components/workflow/nodes/ControlNode'
import {
  WORKFLOW_NODE_CONFIG,
  WORKFLOW_STATUS_CONFIG,
  type WorkflowNodeType,
} from '@/types/workflow'

const nodeTypes: NodeTypes = {
  // Lead workflow nodes
  trigger_manual: TriggerNode,
  trigger_new_lead: TriggerNode,
  action_linkedin_message: ActionNode,
  action_linkedin_connect: ActionNode,
  action_linkedin_like: ActionNode,
  action_linkedin_comment: ActionNode,
  action_send_email: ActionNode,
  action_task: ActionNode,
  condition_connection_accepted: ConditionNode,
  condition_message_received: ConditionNode,
  condition_lead_attribute: ConditionNode,
  condition_time_elapsed: ConditionNode,
  delay_wait: DelayNode,
  // Agent workflow nodes
  trigger_scheduled: ControlNode,
  action_agent_skill: AgentNode,
  action_agent_task: AgentNode,
  action_agent_review: AgentNode,
  action_notify_human: AgentNode,
  action_for_each: ControlNode,
  action_retry: ControlNode,
  condition_task_result: ControlNode,
  condition_human_approval: ControlNode,
}

let nodeId = 0
function getNodeId() {
  return `node_${++nodeId}_${Date.now()}`
}

// ────────────────────────────────────────────────────────────────────────
// validateGraph (CA-032)
// A graph that process-workflow cannot execute (no trigger, cycles, empty
// required agent fields) is worse than no graph — it silently fails runs or,
// with a cycle, re-fires actions every tick (the shape behind the $25 incident).
// Returns a list of human-readable errors; empty === safe to persist/activate.
// ────────────────────────────────────────────────────────────────────────
function validateGraph(nodes: Node[], edges: { source: string; target: string }[]): string[] {
  const errors: string[] = []
  if (nodes.length === 0) {
    errors.push('El workflow no tiene nodos.')
    return errors
  }

  // 1. Exactly one trigger node is required (the executor starts from it).
  const triggerNodes = nodes.filter((n) => (n.type || '').startsWith('trigger_'))
  if (triggerNodes.length === 0) {
    errors.push('Falta un nodo de trigger (el workflow no puede iniciar).')
  } else if (triggerNodes.length > 1) {
    errors.push('Hay más de un nodo de trigger. Deja solo uno.')
  }

  // 2. Scheduled triggers must have a cron set, else the scheduler skips them.
  const scheduled = triggerNodes.find((n) => n.type === 'trigger_scheduled')
  if (scheduled && !((scheduled.data as Record<string, unknown>)?.cron)) {
    errors.push('El trigger programado no tiene un horario (cron) definido.')
  }

  // 3. Agent action nodes need agentId (+ skillName for skill nodes), otherwise
  //    the task is created without an assigned agent → /execute goes nowhere.
  for (const n of nodes) {
    const d = (n.data as Record<string, unknown>) || {}
    const label = (d.label as string) || n.type || n.id
    if (n.type === 'action_agent_skill' || n.type === 'action_agent_task' || n.type === 'action_agent_review') {
      if (!d.agentId) errors.push(`"${label}": selecciona un agente.`)
    }
    if (n.type === 'action_agent_skill' && !d.skillName) {
      errors.push(`"${label}": selecciona una skill.`)
    }
  }

  // 4. Cycle detection (DFS). A→B→A would re-run actions on every tick.
  const adjacency = new Map<string, string[]>()
  for (const e of edges) {
    if (!adjacency.has(e.source)) adjacency.set(e.source, [])
    adjacency.get(e.source)!.push(e.target)
  }
  const WHITE = 0, GRAY = 1, BLACK = 2
  const color = new Map<string, number>()
  nodes.forEach((n) => color.set(n.id, WHITE))
  let hasCycle = false
  const visit = (id: string) => {
    color.set(id, GRAY)
    for (const next of adjacency.get(id) || []) {
      const c = color.get(next)
      if (c === GRAY) { hasCycle = true; return }
      if (c === WHITE) {
        visit(next)
        if (hasCycle) return
      }
    }
    color.set(id, BLACK)
  }
  for (const n of nodes) {
    if (color.get(n.id) === WHITE) visit(n.id)
    if (hasCycle) break
  }
  if (hasCycle) {
    errors.push('El grafo tiene un ciclo. El ejecutor volvería a disparar acciones en cada tick.')
  }

  return errors
}

export function WorkflowBuilder() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isAgentMode = location.pathname.startsWith('/agents/workflows')
  const basePath = isAgentMode ? '/agents/workflows' : '/workflows'
  const { workflows, updateWorkflow, saveGraph, activateWorkflow, pauseWorkflow } = useWorkflow()
  const reactFlowWrapper = useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null)

  const workflow = workflows.find((w) => w.id === id)

  const [nodes, setNodes, onNodesChange] = useNodesState(
    workflow?.graph_json?.nodes || []
  )
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    workflow?.graph_json?.edges || []
  )
  const [selectedNode, setSelectedNode] = useState<Node | null>(null)
  const [workflowName, setWorkflowName] = useState(workflow?.name || '')
  const [saving, setSaving] = useState(false)

  // Sync when workflow data loads
  useEffect(() => {
    if (workflow) {
      setNodes(workflow.graph_json?.nodes || [])
      setEdges(workflow.graph_json?.edges || [])
      setWorkflowName(workflow.name)
    }
  }, [workflow?.id])

  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      const edgeStyle = params.sourceHandle === 'no'
        ? { stroke: '#ef4444', strokeWidth: 2 }
        : params.sourceHandle === 'yes'
          ? { stroke: '#22c55e', strokeWidth: 2 }
          : { strokeWidth: 2 }

      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: true,
            style: edgeStyle,
            label: params.sourceHandle === 'yes' ? 'Yes' : params.sourceHandle === 'no' ? 'No' : undefined,
          },
          eds
        )
      )
    },
    [setEdges]
  )

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()

      const nodeType = event.dataTransfer.getData('application/reactflow') as WorkflowNodeType
      if (!nodeType || !reactFlowInstance || !reactFlowWrapper.current) return

      const config = WORKFLOW_NODE_CONFIG[nodeType]
      if (!config) return

      // screenToFlowPosition already handles the viewport offset — pass raw client coords
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })

      const newNode: Node = {
        id: getNodeId(),
        type: nodeType,
        position,
        data: { ...config.defaultData },
      }

      setNodes((nds) => [...nds, newNode])
    },
    [reactFlowInstance, setNodes]
  )

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      setSelectedNode(node)
    },
    []
  )

  const onPaneClick = useCallback(() => {
    setSelectedNode(null)
  }, [])

  const handleNodeDataUpdate = useCallback(
    (updatedNodeId: string, data: Record<string, any>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === updatedNodeId ? { ...n, data } : n))
      )
      setSelectedNode((prev) =>
        prev && prev.id === updatedNodeId ? { ...prev, data } : prev
      )
    },
    [setNodes]
  )

  // CA-033: handleSave now RETHROWS on failure so callers (handleActivate) can
  // abort instead of activating the stale DB graph. It still toasts on error.
  const handleSave = useCallback(async () => {
    if (!id) return
    setSaving(true)
    try {
      if (workflowName !== workflow?.name) {
        await updateWorkflow(id, { name: workflowName })
      }
      await saveGraph(id, { nodes, edges })
      toast.success('Workflow guardado')
    } catch (error) {
      toast.error('Failed to save workflow')
      console.error(error)
      throw error
    } finally {
      setSaving(false)
    }
  }, [id, workflowName, workflow?.name, nodes, edges, updateWorkflow, saveGraph])

  // Standalone "Guardar" button handler. Saving a draft is always allowed, but
  // we surface validation issues as a non-blocking warning so the user knows the
  // graph is not yet activatable. Swallows the rethrow from handleSave so the
  // button's onClick does not produce an unhandled rejection.
  const handleSaveClick = useCallback(async () => {
    try {
      await handleSave()
      const errors = validateGraph(nodes, edges)
      if (errors.length > 0) {
        toast.warning(`Guardado, pero no activable: ${errors[0]}`, {
          description: errors.length > 1 ? `y ${errors.length - 1} problema(s) más.` : undefined,
        })
      }
    } catch {
      /* handleSave already toasted the error */
    }
  }, [handleSave, nodes, edges])

  const handleActivate = async () => {
    if (!id) return
    // CA-032: block activation of an unexecutable graph. process-workflow cannot
    // run a graph with no trigger / a cycle / empty agent fields.
    const errors = validateGraph(nodes, edges)
    if (errors.length > 0) {
      toast.error(`No se puede activar: ${errors[0]}`, {
        description: errors.length > 1 ? `${errors.length - 1} problema(s) más por resolver.` : undefined,
      })
      return
    }
    // CA-033: if the save fails, do NOT activate — we'd be activating the old
    // graph from the DB, different from what's on screen. handleSave rethrows.
    try {
      await handleSave()
    } catch {
      return
    }
    try {
      await activateWorkflow(id)
      toast.success('Workflow activado')
    } catch (error) {
      toast.error('Failed to activate workflow')
      console.error(error)
    }
  }

  const handlePause = async () => {
    if (!id) return
    try {
      await pauseWorkflow(id)
      toast.success('Workflow pausado')
    } catch (error) {
      toast.error('Failed to pause workflow')
    }
  }

  if (!workflow) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  const statusConfig = WORKFLOW_STATUS_CONFIG[workflow.status]

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(basePath)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Input
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
            className="h-8 w-[250px] text-sm font-semibold border-none shadow-none focus-visible:ring-0 px-1"
          />
          <Badge variant={workflow.status === 'active' ? 'default' : 'secondary'}>
            {statusConfig.label}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`${basePath}/${id}/runs`)}
          >
            <Users className="mr-2 h-4 w-4" />
            Runs
          </Button>
          {workflow.status === 'active' ? (
            <Button variant="outline" size="sm" onClick={handlePause}>
              <Pause className="mr-2 h-4 w-4" />
              Pause
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleActivate}>
              <Play className="mr-2 h-4 w-4" />
              Activate
            </Button>
          )}
          <Button size="sm" onClick={handleSaveClick} disabled={saving}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      {/* Canvas area */}
      <div className="flex flex-1 overflow-hidden">
        <NodePalette />

        <div className="flex-1" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            fitView
            deleteKeyCode={['Backspace', 'Delete']}
            className="bg-background"
          >
            <Controls className="!bg-background !border !shadow-sm" />
            <MiniMap
              className="!bg-background !border !shadow-sm"
              nodeColor={(node) => {
                const type = node.type || ''
                if (type.startsWith('trigger_')) return '#10b981'
                if (type.startsWith('action_')) return '#3b82f6'
                if (type.startsWith('condition_')) return '#f59e0b'
                if (type.startsWith('delay_')) return '#64748b'
                return '#6b7280'
              }}
            />
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
            <Panel position="top-center">
              <div className="rounded-lg bg-background/80 backdrop-blur-sm border px-3 py-1.5 text-xs text-muted-foreground">
                Drag nodes from the palette on the left. Connect them by dragging between handles.
              </div>
            </Panel>
          </ReactFlow>
        </div>

        {selectedNode && (
          <NodeConfigPanel
            node={selectedNode}
            onUpdate={handleNodeDataUpdate}
            onClose={() => setSelectedNode(null)}
          />
        )}
      </div>
    </div>
  )
}
